import {
  FocusEvent,
  KeyboardEvent,
  Fragment,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { CreatableProps } from 'react-select/creatable';
import {
  ActionMeta,
  OnChangeValue,
  FormatOptionLabelMeta,
  SelectInstance,
  GroupBase,
} from 'react-select';
import groupBy from 'lodash/groupBy';

import { COMMA_KEYCODE, ENTER_KEYCODE } from 'lib/strings/strings';
import { CreatableStyled } from '../tag-autocomplete.style';
import {
  OutcomeOption,
  OptionWithKey,
  OutcomeError,
} from './tag-autocomplete-outcomes.constants';
import {
  ErrorsToggleStyled,
  ErrorPillStyled,
  ErrorMessageContainer,
} from 'approot/account/shared/allocate/allocate-errors.style';
import ErrorMessageStyled from 'approot/shared/error-message/error-message.style';
import {
  doValidateInput,
  filterInput,
  isToBeFilteredOnName,
} from './tag-autocomplete-outcomes.utils';
import stringToArray from 'lib/logic/string-to-array';
import {
  spacing8,
  spacing12,
  spacing24,
} from 'approot/shared/styles/spacing.style';
import { SecondaryButtonAndIcon } from 'approot/shared/styles/buttons/buttons';
import IconPlus from 'approot/shared/icons/icon-plus';
import { PStyled, SmallStyled } from 'approot/shared/styles/typography.style';
import { TagOutcomeText } from './tag-autocomplete-outcomes.style';

type Props = Omit<
  CreatableProps<OutcomeOption, boolean, GroupBase<OutcomeOption>>,
  'options'
> & {
  options: OutcomeOption[];
  values: OutcomeOption[] | undefined;
  allOtherOptions: OptionWithKey[];
  onClickOtherOption?: (option: OptionWithKey) => void;
  optionsId?: string;
  networkErrors?: OutcomeError[];
  fieldName?: string;
  // Callers pass a range of shapes (single option, array, or the raw
  // react-select change value) depending on isMulti, so this stays loose.
  handleChange?: (value: any) => void;
};

const renderOptionsLabel = (
  option: OutcomeOption,
  labelMeta: FormatOptionLabelMeta<OutcomeOption>
) => {
  if (labelMeta.context === 'menu') {
    return (
      <>
        <TagOutcomeText>{option.label}</TagOutcomeText>
        <SmallStyled>{option.description}</SmallStyled>
      </>
    );
  }
  return option.label;
};

const TagAutocompleteOutcomes = ({
  options,
  allOtherOptions,
  onClickOtherOption,
  optionsId,
  values,
  handleChange,
  networkErrors,
  ...props
}: Props) => {
  const creatableRef = useRef<SelectInstance<OutcomeOption, boolean> | null>(
    null
  );
  const [detailsExpanded, setDetailsExpanded] = useState<boolean>(false);
  const [invalidCodes, setInvalidCodes] = useState<string[]>();
  const [alreadyAdded, setAlreadyAdded] = useState<string[]>();
  const [otherOptions, setOtherOptions] = useState<OptionWithKey[]>();

  const currentValue = values;

  const clearErrors = useCallback(() => {
    setInvalidCodes(undefined);
    setAlreadyAdded(undefined);
    setOtherOptions(undefined);
    setDetailsExpanded(false);
  }, [setAlreadyAdded, setInvalidCodes, setOtherOptions, setDetailsExpanded]);

  useEffect(() => {
    clearErrors();
  }, [clearErrors, optionsId]);

  const displayErrors = useCallback(
    (
      invalidCodes: string[],
      alreadyAdded: string[],
      otherOptions: OptionWithKey[]
    ) => {
      setInvalidCodes(invalidCodes);
      setAlreadyAdded(alreadyAdded);
      setOtherOptions(otherOptions);
    },
    [setInvalidCodes, setAlreadyAdded, setOtherOptions]
  );

  const validateInput = useCallback(
    (values: string[]) => {
      return doValidateInput(currentValue, values, options, allOtherOptions);
    },
    [options, allOtherOptions, currentValue]
  );

  const handleOnBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      if (event.target.value.length > 0) {
        event.preventDefault();
        const values = stringToArray(event.target.value);
        const result = validateInput(values);

        const hasInvalid = result.invalid.length > 0;
        const hasAlreadyAdded = result.alreadyAdded.length > 0;
        const hasOtherOptions = result.otherOptions.length > 0;
        const hasErrors = hasInvalid || hasAlreadyAdded || hasOtherOptions;

        if (result.valid.length > 0) {
          const allValues = [...(currentValue || []), ...result.valid];
          handleChange && handleChange(allValues);
          if (!hasErrors) {
            clearErrors();
          }
        }

        if (hasErrors) {
          displayErrors(
            result.invalid,
            result.alreadyAdded,
            result.otherOptions
          );
        } else {
          clearErrors();
        }
      }
    },
    [displayErrors, clearErrors, currentValue, validateInput, handleChange]
  );

  const handleOnChange = useCallback(
    (
      value: OnChangeValue<OutcomeOption, boolean>,
      { action }: ActionMeta<OutcomeOption>
    ) => {
      handleChange && handleChange(value);
      if (action === 'select-option') {
        clearErrors();
      }
    },
    [clearErrors, handleChange]
  );

  const handleOnKeyDown = useCallback((event: KeyboardEvent<HTMLElement>) => {
    if (
      event.keyCode === ENTER_KEYCODE &&
      creatableRef.current?.state.focusedOption == null
    ) {
      // no menu item is highlighted
      event.preventDefault();
      (event.target as HTMLInputElement).blur();
      (event.target as HTMLInputElement).focus();
    }

    if (event.keyCode === COMMA_KEYCODE) {
      event.preventDefault();
      (event.target as HTMLInputElement).blur();
      (event.target as HTMLInputElement).focus();
    }
  }, []);

  const handleToggleDetails = useCallback(() => {
    setDetailsExpanded(!detailsExpanded);
  }, [setDetailsExpanded, detailsExpanded]);

  const handleOtherOptionClick = useCallback(
    (option: OptionWithKey) => {
      onClickOtherOption && onClickOtherOption(option);

      // remove added code from error list
      if (otherOptions) {
        setOtherOptions(
          otherOptions.filter(otherOption => otherOption.value !== option.value)
        );
      }
    },
    [otherOptions, onClickOtherOption]
  );

  const handleTryAgain = useCallback(() => {
    if (networkErrors) {
      const oldValue = currentValue;
      const newValue = networkErrors.reduce((value, optionError) => {
        const { type, ...option } = optionError;
        return type === 'add'
          ? [...(value || []), option]
          : value?.filter(opt => opt.value !== option.value);
      }, oldValue);
      handleChange && handleChange(newValue);
    }
  }, [currentValue, handleChange, networkErrors]);

  const otherOutcomeOtions = groupBy(otherOptions, 'key');

  const renderOutcomeErrors = (
    <>
      {invalidCodes && invalidCodes.length > 0 && (
        <ErrorMessageContainer>
          <ErrorMessageStyled>
            These outcomes are not available. Outcomes must match the year,
            subject and curriculum:
          </ErrorMessageStyled>
          {invalidCodes.map((code, index) => {
            return <ErrorPillStyled key={index}>{code}</ErrorPillStyled>;
          })}
        </ErrorMessageContainer>
      )}
      {alreadyAdded && alreadyAdded.length > 0 && (
        <ErrorMessageContainer>
          <ErrorMessageStyled>
            These outcomes have already been added to this lesson:
          </ErrorMessageStyled>
          {alreadyAdded.map((code, index) => {
            return <ErrorPillStyled key={index}>{code}</ErrorPillStyled>;
          })}
        </ErrorMessageContainer>
      )}
      {otherOptions && otherOptions.length > 0 && (
        <>
          <ErrorMessageContainer>
            <ErrorMessageStyled>
              These outcomes do not match this curriculum but can be added:
            </ErrorMessageStyled>
          </ErrorMessageContainer>
          {Object.keys(otherOutcomeOtions).map(key => {
            return (
              <Fragment key={key}>
                <PStyled marginBottom={spacing8} marginTop={spacing12}>
                  Add to {otherOutcomeOtions[key][0].groupLabel}
                </PStyled>
                {otherOutcomeOtions[key].map(option => {
                  return (
                    <SecondaryButtonAndIcon
                      type="button"
                      $isSmall
                      key={option.value}
                      onClick={handleOtherOptionClick.bind(null, option)}
                    >
                      <span>{option.label}</span>
                      <IconPlus width={12} height={12} />
                    </SecondaryButtonAndIcon>
                  );
                })}
              </Fragment>
            );
          })}
        </>
      )}
    </>
  );

  const getNoOptionsMessage = ({ inputValue }: { inputValue: string }) => {
    let message: string | null = 'No more options are available';

    if (inputValue.length > 0) {
      message =
        // If it's a comma separated or space separated pasted value, show nothing
        inputValue.includes(',') || inputValue.includes(' ')
          ? null
          : 'This outcome is not available. It must match the Year, Subject and Curriculum.';
    }
    return message;
  };

  return (
    <>
      <CreatableStyled
        ref={creatableRef}
        value={currentValue}
        onChange={handleOnChange}
        onBlur={handleOnBlur}
        options={options}
        onKeyDown={(e: KeyboardEvent<HTMLElement>) => handleOnKeyDown(e)}
        isClearable={false}
        classNamePrefix="Select"
        blurInputOnSelect={false}
        menuShouldScrollIntoView={false}
        isValidNewOption={() => false} // turns off ability to add new options
        noOptionsMessage={getNoOptionsMessage}
        formatOptionLabel={renderOptionsLabel}
        filterOption={
          isToBeFilteredOnName(options?.[0])
            ? (
                candidate: {
                  label: string;
                  data: OutcomeOption;
                },
                input: string
              ) => filterInput(input, candidate)
            : undefined
        }
        {...props}
      />
      {((invalidCodes && invalidCodes.length > 0) ||
        (alreadyAdded && alreadyAdded.length > 0) ||
        (otherOptions && otherOptions.length > 0)) && (
        <>
          <ErrorMessageStyled $bold $marginBottom={spacing24}>
            Some of the outcomes could not be added.{' '}
            <ErrorsToggleStyled
              type="button"
              onClick={handleToggleDetails}
              data-selector="view-hide-error-details"
            >
              {detailsExpanded ? 'Hide details' : 'View details'}
            </ErrorsToggleStyled>
          </ErrorMessageStyled>
          {detailsExpanded && renderOutcomeErrors}
        </>
      )}
      {networkErrors && networkErrors.length > 0 && (
        <ErrorMessageContainer>
          <ErrorMessageStyled>
            These was an error updating these outcomes:
          </ErrorMessageStyled>
          {networkErrors.map((option, index) => {
            return (
              <ErrorPillStyled key={index}>{option.label}</ErrorPillStyled>
            );
          })}
          <ErrorsToggleStyled
            type="button"
            onClick={handleTryAgain}
            data-selector="view-hide-error-details"
          >
            Try again
          </ErrorsToggleStyled>
        </ErrorMessageContainer>
      )}
    </>
  );
};

export default TagAutocompleteOutcomes;
